import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getClientById, updateClient, getAllClients } from '../utils/apiService';
import { prepareClientForDisplay } from '../utils/customFields/customFieldsHelper';
import { ramOptions, cpuOptions, diskOptions, osOptions, mariadbVersionOptions, apacheVersionOptions, nodejsVersionOptions } from '../data/predefinedValues';
import { getEmptyServer, getEmptyLocalModification, serverTypes } from '../utils/serverModel';
import ServerForm from '../components/ServerForm';
import TeleMaintenanceConfig from '../components/TeleMaintenanceConfig';
import ConnectionProcedureEditor from '../components/ConnectionProcedureEditor'; // NOUVEAU : Éditeur de procédures

const EditClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [showLocalModifications, setShowLocalModifications] = useState({});
  const [sslInfo, setSslInfo] = useState({
    patientPortal: null,
    carePortal: null,
    loading: false
  });

  // État pour gérer le client et ses environnements associés
  const [clientData, setClientData] = useState({
    base: {
      name: '',
      description: '',
      connectionProcedure: '',
      remoteMaintenance: false,
      slackChannelId: '',
      bastionConfig: { // NOUVEAU : Configuration du bastion
        enabled: false,
        url: '',
        displayName: ''
      },
      telemaintenance: {
        enabled: false,
        recipientEmail: '',
        ccEmails: [],
        defaultAccount: '',
        templateType: 'generic',
        customTemplate: '',
        defaultTemplate: ''
      }
    },
    environments: {
      production: {
        id: null,
        enabled: false,
        patientPortalUrl: '',
        carePortalUrl: '',
        patientPortalUrlExternallyAccessible: true,
        carePortalUrlExternallyAccessible: true,
        checkGeneral: '',
        servers: []
      },
      qualification: {
        id: null,
        enabled: false,
        patientPortalUrl: '',
        carePortalUrl: '',
        patientPortalUrlExternallyAccessible: true,
        carePortalUrlExternallyAccessible: true,
        checkGeneral: '',
        servers: []
      }
    }
  });

  useEffect(() => {
    const loadClientAndRelated = async () => {
      // Charger le client principal
      const mainClient = await getClientById(id);
      if (!mainClient) {
        setLoading(false);
        return;
      }

      // Préparer les données pour l'affichage
      const preparedMainClient = prepareClientForDisplay(mainClient, { 
        osOptions, ramOptions, cpuOptions, diskOptions, 
        mariadbVersionOptions, apacheVersionOptions, nodejsVersionOptions 
      });

      // Déterminer le nom de base (sans QUAL)
      const baseName = mainClient.name.replace(/\s*QUAL\s*/gi, '').trim();
      
      // Déterminer l'environnement du client principal
      const isMainProduction = !mainClient.name.toLowerCase().includes('qual') && 
                              (mainClient.environment?.toLowerCase().includes('production') || !mainClient.environment?.toLowerCase().includes('qualification'));
      
      // Initialiser les données du client
      const newClientData = {
        base: {
          name: baseName,
          description: mainClient.description || '',
          connectionProcedure: mainClient.connectionProcedure || '',
          remoteMaintenance: mainClient.remoteMaintenance || false,
          slackChannelId: mainClient.slackChannelId || '',
          bastionConfig: mainClient.bastionConfig || { // NOUVEAU : Configuration bastion
            enabled: false,
            url: '',
            displayName: ''
          },
          telemaintenance: mainClient.telemaintenance || {
            enabled: false,
            recipientEmail: '',
            ccEmails: [],
            defaultAccount: '',
            templateType: 'generic',
            customTemplate: '',
            defaultTemplate: ''
          }
        },
        environments: {
          production: {
            id: null,
            enabled: false,
            patientPortalUrl: '',
            carePortalUrl: '',
            patientPortalUrlExternallyAccessible: true,
            carePortalUrlExternallyAccessible: true,
        checkGeneral: '',
        servers: []
          },
          qualification: {
            id: null,
            enabled: false,
            patientPortalUrl: '',
            carePortalUrl: '',
            patientPortalUrlExternallyAccessible: true,
            carePortalUrlExternallyAccessible: true,
            checkGeneral: '',
            servers: []
          }
        }
      };

      // Remplir l'environnement du client principal
      const envKey = isMainProduction ? 'production' : 'qualification';
      newClientData.environments[envKey] = {
        id: mainClient.id,
        enabled: true,
        patientPortalUrl: mainClient.patientPortalUrl || '',
        carePortalUrl: mainClient.carePortalUrl || '',
        patientPortalUrlExternallyAccessible: mainClient.patientPortalUrlExternallyAccessible !== false,
        carePortalUrlExternallyAccessible: mainClient.carePortalUrlExternallyAccessible !== false,
        checkGeneral: mainClient.checkGeneral?.[isMainProduction ? 'prod' : 'qual'] || '',
        servers: prepareServers(preparedMainClient.servers || [])
      };

      // Chercher un client associé (PROD/QUAL)
      const allClients = await getAllClients();
      const relatedClient = allClients.find(c => {
        if (c.id === mainClient.id) return false;
        const cBaseName = c.name.replace(/\s*QUAL\s*/gi, '').trim();
        return cBaseName === baseName;
      });

      if (relatedClient) {
        const preparedRelatedClient = prepareClientForDisplay(relatedClient, { 
          osOptions, ramOptions, cpuOptions, diskOptions, 
          mariadbVersionOptions, apacheVersionOptions, nodejsVersionOptions 
        });

        const isRelatedProduction = !relatedClient.name.toLowerCase().includes('qual') && 
                                   (relatedClient.environment?.toLowerCase().includes('production') || !relatedClient.environment?.toLowerCase().includes('qualification'));
        
        const relatedEnvKey = isRelatedProduction ? 'production' : 'qualification';
        newClientData.environments[relatedEnvKey] = {
          id: relatedClient.id,
          enabled: true,
          patientPortalUrl: relatedClient.patientPortalUrl || '',
          carePortalUrl: relatedClient.carePortalUrl || '',
          patientPortalUrlExternallyAccessible: relatedClient.patientPortalUrlExternallyAccessible !== false,
          carePortalUrlExternallyAccessible: relatedClient.carePortalUrlExternallyAccessible !== false,
          checkGeneral: relatedClient.checkGeneral?.[isRelatedProduction ? 'prod' : 'qual'] || '',
          servers: prepareServers(preparedRelatedClient.servers || [])
        };

        // Utiliser les informations communes du client le plus récent
        if (new Date(relatedClient.updatedAt) > new Date(mainClient.updatedAt)) {
          newClientData.base.description = relatedClient.description || newClientData.base.description;
          newClientData.base.connectionProcedure = relatedClient.connectionProcedure || newClientData.base.connectionProcedure;
          newClientData.base.remoteMaintenance = relatedClient.remoteMaintenance || newClientData.base.remoteMaintenance;
          newClientData.base.slackChannelId = relatedClient.slackChannelId || newClientData.base.slackChannelId;
        }
      }

      setClientData(newClientData);
      
      // Définir l'onglet actif selon l'environnement actuel
      if (isMainProduction && newClientData.environments.production.enabled) {
        setActiveTab('production');
      } else if (!isMainProduction && newClientData.environments.qualification.enabled) {
        setActiveTab('qualification');
      }
      
      setLoading(false);
    };

    loadClientAndRelated();
  }, [id]);

  // Gestion des informations générales du client
  const handleBaseInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setClientData({
      ...clientData,
      base: {
        ...clientData.base,
        [name]: type === 'checkbox' ? checked : value
      }
    });
  };

  // Gestion de la configuration télémaintenance
  const handleTeleMaintenanceChange = (teleMaintenanceConfig) => {
    setClientData({
      ...clientData,
      base: {
        ...clientData.base,
        telemaintenance: teleMaintenanceConfig
      }
    });
  };
  
  // NOUVEAU : Gestion de la configuration bastion
  const handleBastionConfigChange = (field, value) => {
    setClientData({
      ...clientData,
      base: {
        ...clientData.base,
        bastionConfig: {
          ...clientData.base.bastionConfig,
          [field]: value
        }
      }
    });
  };

  // Fonction pour préparer les serveurs avec les bonnes couleurs et types
  const prepareServers = (servers) => {
    return servers.map(server => {
      let updatedServer = {
        ...server,
        localModifications: server.localModifications || {
          patientApp: [], managerFrontApp: [], marshaller: [], marshallerInterop: []
        }
      };
      
      if (server.type) {
        const typeObj = serverTypes.find(type => type.value === server.type);
        if (typeObj) {
          updatedServer.color = typeObj.color;
        } else {
          updatedServer.type = 'Serveur tout-en-un';
          updatedServer.color = '#3B82F6';
        }
      } else {
        updatedServer.type = 'Serveur tout-en-un';
        updatedServer.color = '#3B82F6';
      }
      
      return updatedServer;
    });
  };

  const handleEnvInputChange = (env, field, value) => {
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          [field]: value
        }
      }
    });
  };

  const handleServerChange = (env, index, e) => {
    const { name, value, type, checked } = e.target;
    const updatedServers = [...(clientData.environments[env].servers || [])];
    
    updatedServers[index] = {
      ...updatedServers[index],
      [name]: type === 'checkbox' ? checked : value
    };
    
    // Gérer le changement de type de serveur
    if (name === 'type') {
      const typeObj = serverTypes.find(t => t.value === value);
      if (typeObj) {
        updatedServers[index].color = typeObj.color;
      }
      
      // Réinitialiser les champs selon le type
      const fieldVisibility = {
        'Serveur applicatif': {
          mariadbVersion: false,
          dbInstallFolder: false,
          dbOptimized: false
        },
        'Serveur base de données': {
          apacheVersion: false,
          nodejsVersion: false,
          gitPatientAppVersion: false,
          gitManagerFrontAppVersion: false,
          gitMarshallerVersion: false,
          gitMarshallerInteropVersion: false,
          probeDeployed: false,
          appInstallFolder: false
        },
        'Serveur interop': {
          mariadbVersion: false,
          dbInstallFolder: false,
          dbOptimized: false,
          apacheVersion: false,
          gitPatientAppVersion: false,
          gitManagerFrontAppVersion: false
        }
      };
      
      const fieldsToReset = fieldVisibility[value];
      if (fieldsToReset) {
        Object.keys(fieldsToReset).forEach(field => {
          if (!fieldsToReset[field]) {
            updatedServers[index][field] = '';
          }
        });
      }
    }
    
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: updatedServers
        }
      }
    });
  };

  const addServer = (env) => {
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: [...(clientData.environments[env].servers || []), getEmptyServer()]
        }
      }
    });
  };

  const removeServer = (env, index) => {
    const updatedServers = [...(clientData.environments[env].servers || [])];
    updatedServers.splice(index, 1);
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: updatedServers
        }
      }
    });
  };

  const duplicateServer = (env, index) => {
    const servers = clientData.environments[env].servers || [];
    const serverToDuplicate = JSON.parse(JSON.stringify(servers[index]));
    serverToDuplicate.name = serverToDuplicate.name 
      ? `${serverToDuplicate.name} (copie)` 
      : `Serveur ${index + 1} (copie)`;
    
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: [...servers, serverToDuplicate]
        }
      }
    });
  };

  // Gestion des modifications locales
  const addLocalModification = (env, serverIndex, component) => {
    const key = `${env}-${serverIndex}-${component}`;
    const updatedServers = [...(clientData.environments[env].servers || [])];
    
    if (!updatedServers[serverIndex].localModifications) {
      updatedServers[serverIndex].localModifications = {
        patientApp: [],
        managerFrontApp: [],
        marshaller: [],
        marshallerInterop: []
      };
    }
    
    updatedServers[serverIndex].localModifications[component].push(getEmptyLocalModification());
    
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: updatedServers
        }
      }
    });
    
    setShowLocalModifications({ ...showLocalModifications, [key]: true });
  };

  const handleLocalModificationChange = (env, serverIndex, component, modIndex, field, value) => {
    const updatedServers = [...(clientData.environments[env].servers || [])];
    updatedServers[serverIndex].localModifications[component][modIndex][field] = value;
    
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: updatedServers
        }
      }
    });
  };

  const removeLocalModification = (env, serverIndex, component, modIndex) => {
    const updatedServers = [...(clientData.environments[env].servers || [])];
    updatedServers[serverIndex].localModifications[component].splice(modIndex, 1);
    
    setClientData({
      ...clientData,
      environments: {
        ...clientData.environments,
        [env]: {
          ...clientData.environments[env],
          servers: updatedServers
        }
      }
    });
  };

  // Vérification SSL
  const checkSSL = async (env, portalType) => {
    const url = portalType === 'patient' 
      ? clientData.environments[env].patientPortalUrl 
      : clientData.environments[env].carePortalUrl;
    
    if (!url) {
      alert(`Veuillez d'abord saisir l'URL du portail ${portalType === 'patient' ? 'patient' : 'soignant'}.`);
      return;
    }
    
    setSslInfo(prev => ({ ...prev, loading: true }));
    
    try {
      // Importer les utilitaires d'auth
      const { fetchWithAuth } = await import('../utils/authUtils');
      
      const response = await fetchWithAuth(`/api/check-ssl?url=${encodeURIComponent(url)}`);
      const data = await response.json();
      
      if (response.ok) {
        setSslInfo(prev => ({
          ...prev,
          [`${env}-${portalType}`]: data,
          loading: false
        }));
      } else {
        alert(`Erreur: ${data.error}`);
        setSslInfo(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      alert(`Erreur lors de la vérification du certificat: ${error.message}`);
      setSslInfo(prev => ({ ...prev, loading: false }));
    }
  };

  // Soumission du formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Importer addClient si nécessaire
      const { addClient } = await import('../utils/apiService');
      
      // Mettre à jour chaque environnement
      for (const [envKey, envData] of Object.entries(clientData.environments)) {
        if (envData.enabled) {
          // Préparer les données du client
          const clientToSave = {
            name: envKey === 'qualification' && clientData.environments.production.enabled 
              ? `${clientData.base.name} QUAL` 
              : clientData.base.name,
            description: clientData.base.description,
            environment: envKey === 'production' ? 'Production' : 'Qualification',
            connectionProcedure: window.FORCED_CONNECTION_PROCEDURE || clientData.base.connectionProcedure,
            remoteMaintenance: clientData.base.remoteMaintenance,
            slackChannelId: clientData.base.slackChannelId,
            bastionConfig: clientData.base.bastionConfig, // NOUVEAU : Configuration bastion
            telemaintenance: clientData.base.telemaintenance, // AJOUTÉ : Données télémaintenance
            patientPortalUrl: envData.patientPortalUrl,
            carePortalUrl: envData.carePortalUrl,
            patientPortalUrlExternallyAccessible: envData.patientPortalUrlExternallyAccessible,
            carePortalUrlExternallyAccessible: envData.carePortalUrlExternallyAccessible,
            checkGeneral: {
              prod: envKey === 'production' ? envData.checkGeneral : '',
              qual: envKey === 'qualification' ? envData.checkGeneral : ''
            },
            servers: prepareServersForSave(envData.servers)
          };
          
          if (envData.id) {
            // Mettre à jour le client existant
            await updateClient(envData.id, {
              ...clientToSave,
              id: envData.id,
              updatedAt: new Date().toISOString()
            });
          } else {
            // Créer un nouveau client pour cet environnement
            await addClient(clientToSave);
          }
        }
      }
      
      // Naviguer vers le client principal
      const mainId = clientData.environments.production.id || clientData.environments.qualification.id;
      navigate(`/client/${mainId}`);
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      alert('Une erreur est survenue lors de la mise à jour');
    }
  };

  // Préparer les serveurs pour la sauvegarde
  const prepareServersForSave = (servers) => {
    return servers.map(server => {
      const processedServer = { ...server };
      
      // Traiter les champs personnalisés
      if (processedServer.os === 'custom' && processedServer.customOs) {
        processedServer.os = processedServer.customOs;
      }
      if (processedServer.ram === 'custom' && processedServer.customRam) {
        processedServer.ram = processedServer.customRam;
      }
      if (processedServer.cpu === 'custom' && processedServer.customCpu) {
        processedServer.cpu = processedServer.customCpu;
      }
      if (processedServer.diskSpace === 'custom' && processedServer.customDiskSpace) {
        processedServer.diskSpace = processedServer.customDiskSpace;
      }
      if (processedServer.mariadbVersion === 'custom' && processedServer.customMariadbVersion) {
        processedServer.mariadbVersion = processedServer.customMariadbVersion;
      }
      if (processedServer.apacheVersion === 'custom' && processedServer.customApacheVersion) {
        processedServer.apacheVersion = processedServer.customApacheVersion;
      }
      if (processedServer.nodejsVersion === 'custom' && processedServer.customNodejsVersion) {
        processedServer.nodejsVersion = processedServer.customNodejsVersion;
      }

      // Nettoyer les champs custom
      const cleanServer = {};
      Object.keys(processedServer).forEach(key => {
        if (!key.startsWith('custom')) {
          cleanServer[key] = processedServer[key];
        }
      });
      
      return cleanServer;
    });
  };

  // Helpers pour l'affichage
  const getSSLStatusColor = (status) => {
    switch(status) {
      case 'expired': return 'bg-red-100 text-red-800 border-red-300';
      case 'warning': return 'bg-amber-100 text-amber-800 border-amber-300';
      case 'valid': return 'bg-green-100 text-green-800 border-green-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Rendu du formulaire de serveur
  const renderServerForm = (env, server, index) => {
    const servers = clientData.environments[env].servers || [];
    return (
      <ServerForm
        key={`${env}-${index}`}
        server={server}
        index={index}
        env={env}
        onChange={handleServerChange}
        onAddLocalModification={addLocalModification}
        onLocalModificationChange={handleLocalModificationChange}
        onRemoveLocalModification={removeLocalModification}
        showLocalModifications={showLocalModifications}
        onDuplicate={duplicateServer}
        onRemove={removeServer}
        canRemove={servers.length > 1}
      />
    );
  };

  if (loading) return <div className="text-center py-10">Chargement...</div>;
  
  if (!clientData.environments.production.id && !clientData.environments.qualification.id) {
    return (
      <div className="text-center py-10">
        <h2 className="text-2xl font-bold mb-4">Client non trouvé</h2>
        <button onClick={() => navigate('/')} className="text-blue-600 hover:underline">
          Retour à la liste des clients
        </button>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Modifier le client: {clientData.base.name}</h1>
      
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md overflow-hidden">
        {/* Tabs de navigation */}
        <div className="flex border-b">
          <button
            type="button"
            onClick={() => setActiveTab('general')}
            className={`px-6 py-3 font-medium ${
              activeTab === 'general' 
                ? 'border-b-2 border-blue-500 text-blue-600' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Informations générales
          </button>
          {clientData.environments.production.enabled && (
            <button
              type="button"
              onClick={() => setActiveTab('production')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'production' 
                  ? 'border-b-2 border-red-500 text-red-600' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Production
            </button>
          )}
          {clientData.environments.qualification.enabled && (
            <button
              type="button"
              onClick={() => setActiveTab('qualification')}
              className={`px-6 py-3 font-medium ${
                activeTab === 'qualification' 
                  ? 'border-b-2 border-orange-500 text-orange-600' 
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              Qualification
            </button>
          )}
        </div>
        
        <div className="p-6">
          {/* Tab Informations générales */}
          {activeTab === 'general' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Informations générales du client</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="name">
                    Nom du client *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="w-full p-3 border border-gray-300 rounded"
                    value={clientData.base.name}
                    onChange={handleBaseInputChange}
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Ne pas inclure "QUAL" ou "PROD" dans le nom
                  </p>
                </div>
                
                <div>
                  <label className="block text-gray-700 mb-2" htmlFor="slackChannelId">
                    Canal Slack
                  </label>
                  <input
                    type="text"
                    id="slackChannelId"
                    name="slackChannelId"
                    className="w-full p-3 border border-gray-300 rounded"
                    value={clientData.base.slackChannelId}
                    onChange={handleBaseInputChange}
                    placeholder="nom-du-canal"
                  />
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="3"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={clientData.base.description}
                  onChange={handleBaseInputChange}
                ></textarea>
              </div>
              
              {/* NOUVEAU : Éditeur de procédure de connexion avancé */}
              <div className="mb-6">
                <ConnectionProcedureEditor
                  value={clientData.base.connectionProcedure}
                  onChange={(value) => {
                    // Stocker dans une variable globale pour éviter les conflits de state React
                    window.FORCED_CONNECTION_PROCEDURE = value;
                    
                    // Mettre à jour le state normalement
                    const newClientData = {
                      ...clientData,
                      base: {
                        ...clientData.base,
                        connectionProcedure: value
                      }
                    };
                    
                    setClientData(newClientData);
                  }}
                />
              </div>
              
              <div className="mb-6">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="remoteMaintenance"
                    name="remoteMaintenance"
                    className="w-5 h-5 mr-2 border border-gray-300 rounded"
                    checked={clientData.base.remoteMaintenance}
                    onChange={handleBaseInputChange}
                  />
                  <label className="text-gray-700" htmlFor="remoteMaintenance">
                    Télémaintenance à débloquer pour accès aux serveurs
                  </label>
                </div>
              </div>
              
              {/* Statut des environnements */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold mb-4">Environnements configurés</h3>
                <div className="space-y-3">
                  {clientData.environments.production.enabled && (
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-red-200">
                      <div>
                        <span className="font-medium text-red-600">Production</span>
                        <p className="text-sm text-gray-500">
                          {clientData.environments.production.servers.length} serveur(s) configuré(s)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTab('production')}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Modifier →
                      </button>
                    </div>
                  )}
                  
                  {clientData.environments.qualification.enabled && (
                    <div className="flex items-center justify-between p-3 bg-white rounded border border-orange-200">
                      <div>
                        <span className="font-medium text-orange-600">Qualification</span>
                        <p className="text-sm text-gray-500">
                          {(clientData.environments.qualification.servers || []).length} serveur(s) configuré(s)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setActiveTab('qualification')}
                        className="text-sm text-blue-600 hover:underline"
                      >
                        Modifier →
                      </button>
                    </div>
                  )}
                  
                  {!clientData.environments.qualification.enabled && (
                    <div className="flex items-center justify-between p-3 bg-gray-100 rounded border border-gray-300">
                      <div>
                        <span className="font-medium text-gray-600">Qualification</span>
                        <p className="text-sm text-gray-500">
                          Environnement non configuré
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newQualification = {
                            id: null,
                            enabled: true,
                            patientPortalUrl: '',
                            carePortalUrl: '',
                            patientPortalUrlExternallyAccessible: true,
                            carePortalUrlExternallyAccessible: true,
                            checkGeneral: '',
                            servers: [getEmptyServer()]
                          };
                          setClientData(prev => ({
                            ...prev,
                            environments: {
                              ...prev.environments,
                              qualification: newQualification
                            }
                          }));
                          // Utiliser setTimeout pour s'assurer que le state est mis à jour avant de changer d'onglet
                          setTimeout(() => setActiveTab('qualification'), 0);
                        }}
                        className="text-sm bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700"
                      >
                        Ajouter Qualification
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Configuration Télémaintenance */}
              <div className="mt-8">
                <TeleMaintenanceConfig
                  value={clientData.base.telemaintenance}
                  remoteMaintenance={clientData.base.remoteMaintenance}
                  onChange={handleTeleMaintenanceChange}
                  onRemoteMaintenanceChange={(enabled) => setClientData({
                    ...clientData, 
                    base: {...clientData.base, remoteMaintenance: enabled}
                  })}
                />
              </div>
              
              {/* NOUVEAU : Configuration Bastion de Connexion */}
              <div className="mt-8">
                <div className="bg-white p-6 rounded-lg border">
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">🔒 Bastion de Connexion</h3>
                    <div className={`p-4 rounded-lg border ${
                      clientData.base.bastionConfig.enabled 
                        ? 'bg-green-50 border-green-200' 
                        : 'bg-gray-50 border-gray-200'
                    }`}>
                      <div className="flex items-center mb-2">
                        <svg className={`w-5 h-5 mr-2 ${
                          clientData.base.bastionConfig.enabled ? 'text-green-600' : 'text-gray-600'
                        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                        <span className={`font-medium ${
                          clientData.base.bastionConfig.enabled ? 'text-green-800' : 'text-gray-800'
                        }`}>
                          {clientData.base.bastionConfig.enabled 
                            ? 'Bastion de connexion configuré' 
                            : 'Bastion de connexion non configuré'
                          }
                        </span>
                      </div>
                      <p className={`text-sm ${
                        clientData.base.bastionConfig.enabled ? 'text-green-700' : 'text-gray-700'
                      }`}>
                        {clientData.base.bastionConfig.enabled 
                          ? 'Un bouton de connexion au bastion sera affiché sur la fiche client.' 
                          : 'Configurez l\'URL du bastion pour afficher un bouton de connexion rapide sur la fiche client.'
                        }
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Activation du bastion */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="bastionEnabled"
                        checked={clientData.base.bastionConfig.enabled}
                        onChange={(e) => handleBastionConfigChange('enabled', e.target.checked)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="bastionEnabled" className="ml-2 text-sm font-medium text-gray-700">
                        Activer le bastion de connexion pour ce client
                      </label>
                    </div>
                    
                    {/* Configuration quand activé */}
                    {clientData.base.bastionConfig.enabled && (
                      <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              URL du bastion *
                            </label>
                            <input
                              type="url"
                              value={clientData.base.bastionConfig.url}
                              onChange={(e) => handleBastionConfigChange('url', e.target.value)}
                              placeholder="https://bastion.exolis.fr/client/exemple"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              required
                            />
                            <p className="text-xs text-gray-500 mt-1">URL complète vers le bastion de connexion</p>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Nom d'affichage (optionnel)
                            </label>
                            <input
                              type="text"
                              value={clientData.base.bastionConfig.displayName}
                              onChange={(e) => handleBastionConfigChange('displayName', e.target.value)}
                              placeholder="Connexion serveurs {client.name}"
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <p className="text-xs text-gray-500 mt-1">Texte affiché sur le bouton (par défaut: "Bastion")</p>
                          </div>
                        </div>
                        
                        {/* Aperçu */}
                        {clientData.base.bastionConfig.url && (
                          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                            <h4 className="text-sm font-medium text-blue-800 mb-2">👁️ Aperçu du bouton :</h4>
                            <div className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md cursor-not-allowed opacity-75">
                              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-2M7 13l3 3 7-7" />
                              </svg>
                              {clientData.base.bastionConfig.displayName || 'Bastion'}
                            </div>
                            <p className="text-xs text-blue-700 mt-2">
                              Ce bouton apparaîtra sur la fiche client et redirigera vers : <br/>
                              <span className="font-mono bg-blue-100 px-1 rounded">{clientData.base.bastionConfig.url}</span>
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Tab Production */}
          {activeTab === 'production' && clientData.environments.production.enabled && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-red-600">Configuration Production</h2>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-3">URLs Production</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail patient</label>
                    <div className="flex">
                      <input
                        type="url"
                        className="flex-1 p-3 border border-gray-300 rounded-l"
                        value={clientData.environments.production.patientPortalUrl}
                        onChange={(e) => handleEnvInputChange('production', 'patientPortalUrl', e.target.value)}
                        placeholder="https://patient.example.com"
                      />
                      <button 
                        type="button" 
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-r"
                        onClick={() => checkSSL('production', 'patient')}
                        disabled={sslInfo.loading}
                      >
                        SSL
                      </button>
                    </div>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="prod-patient-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={clientData.environments.production.patientPortalUrlExternallyAccessible}
                        onChange={(e) => handleEnvInputChange('production', 'patientPortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="prod-patient-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                    {sslInfo['production-patient'] && (
                      <div className={`mt-2 p-3 border rounded ${getSSLStatusColor(sslInfo['production-patient'].status)}`}>
                        <p className="text-xs">
                          <span className="font-medium">Expire le:</span> {formatDate(sslInfo['production-patient'].validTo)}
                          <span className="ml-2">({sslInfo['production-patient'].daysRemaining} jours)</span>
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail soignant</label>
                    <div className="flex">
                      <input
                        type="url"
                        className="flex-1 p-3 border border-gray-300 rounded-l"
                        value={clientData.environments.production.carePortalUrl}
                        onChange={(e) => handleEnvInputChange('production', 'carePortalUrl', e.target.value)}
                        placeholder="https://care.example.com"
                      />
                      <button 
                        type="button" 
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-r"
                        onClick={() => checkSSL('production', 'care')}
                        disabled={sslInfo.loading}
                      >
                        SSL
                      </button>
                    </div>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="prod-care-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={clientData.environments.production.carePortalUrlExternallyAccessible}
                        onChange={(e) => handleEnvInputChange('production', 'carePortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="prod-care-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                    {sslInfo['production-care'] && (
                      <div className={`mt-2 p-3 border rounded ${getSSLStatusColor(sslInfo['production-care'].status)}`}>
                        <p className="text-xs">
                          <span className="font-medium">Expire le:</span> {formatDate(sslInfo['production-care'].validTo)}
                          <span className="ml-2">({sslInfo['production-care'].daysRemaining} jours)</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Check général Production</label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={clientData.environments.production.checkGeneral}
                  onChange={(e) => handleEnvInputChange('production', 'checkGeneral', e.target.value)}
                  placeholder="Dernière vérification le JJ/MM/AAAA - Status"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Serveurs Production</h3>
                  <button
                    type="button"
                    onClick={() => addServer('production')}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    + Ajouter un serveur
                  </button>
                </div>
                
                {clientData.environments.production.servers.map((server, index) => 
                  renderServerForm('production', server, index)
                )}
              </div>
            </div>
          )}
          
          {/* Tab Qualification */}
          {activeTab === 'qualification' && clientData.environments.qualification.enabled && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-orange-600">Configuration Qualification</h2>
              
              <div className="mb-6">
                <h3 className="font-semibold mb-3">URLs Qualification</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail patient</label>
                    <div className="flex">
                      <input
                        type="url"
                        className="flex-1 p-3 border border-gray-300 rounded-l"
                        value={clientData.environments.qualification.patientPortalUrl}
                        onChange={(e) => handleEnvInputChange('qualification', 'patientPortalUrl', e.target.value)}
                        placeholder="https://qual-patient.example.com"
                      />
                      <button 
                        type="button" 
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-r"
                        onClick={() => checkSSL('qualification', 'patient')}
                        disabled={sslInfo.loading}
                      >
                        SSL
                      </button>
                    </div>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="qual-patient-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={clientData.environments.qualification.patientPortalUrlExternallyAccessible ?? true}
                        onChange={(e) => handleEnvInputChange('qualification', 'patientPortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="qual-patient-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                    {sslInfo['qualification-patient'] && (
                      <div className={`mt-2 p-3 border rounded ${getSSLStatusColor(sslInfo['qualification-patient'].status)}`}>
                        <p className="text-xs">
                          <span className="font-medium">Expire le:</span> {formatDate(sslInfo['qualification-patient'].validTo)}
                          <span className="ml-2">({sslInfo['qualification-patient'].daysRemaining} jours)</span>
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="block text-gray-700 mb-2">URL du portail soignant</label>
                    <div className="flex">
                      <input
                        type="url"
                        className="flex-1 p-3 border border-gray-300 rounded-l"
                        value={clientData.environments.qualification.carePortalUrl}
                        onChange={(e) => handleEnvInputChange('qualification', 'carePortalUrl', e.target.value)}
                        placeholder="https://qual-care.example.com"
                      />
                      <button 
                        type="button" 
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded-r"
                        onClick={() => checkSSL('qualification', 'care')}
                        disabled={sslInfo.loading}
                      >
                        SSL
                      </button>
                    </div>
                    <div className="flex items-center mt-2">
                      <input
                        type="checkbox"
                        id="qual-care-external"
                        className="w-4 h-4 mr-2 border border-gray-300 rounded"
                        checked={clientData.environments.qualification.carePortalUrlExternallyAccessible ?? true}
                        onChange={(e) => handleEnvInputChange('qualification', 'carePortalUrlExternallyAccessible', e.target.checked)}
                      />
                      <label className="text-sm text-gray-600" htmlFor="qual-care-external">
                        Accessible depuis l'extérieur
                      </label>
                    </div>
                    {sslInfo['qualification-care'] && (
                      <div className={`mt-2 p-3 border rounded ${getSSLStatusColor(sslInfo['qualification-care'].status)}`}>
                        <p className="text-xs">
                          <span className="font-medium">Expire le:</span> {formatDate(sslInfo['qualification-care'].validTo)}
                          <span className="ml-2">({sslInfo['qualification-care'].daysRemaining} jours)</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-gray-700 mb-2">Check général Qualification</label>
                <input
                  type="text"
                  className="w-full p-3 border border-gray-300 rounded"
                  value={clientData.environments.qualification.checkGeneral}
                  onChange={(e) => handleEnvInputChange('qualification', 'checkGeneral', e.target.value)}
                  placeholder="Dernière vérification le JJ/MM/AAAA - Status"
                />
              </div>
              
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold">Serveurs Qualification</h3>
                  <button
                    type="button"
                    onClick={() => addServer('qualification')}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    + Ajouter un serveur
                  </button>
                </div>
                
                {(clientData.environments.qualification.servers || []).map((server, index) =>
                  renderServerForm('qualification', server, index)
                )}
              </div>
            </div>
          )}
          
          {/* Boutons d'action */}
          <div className="mt-6 pt-6 border-t flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Enregistrer les modifications
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditClient;